import { getShellLogger } from '../../common/logger'
import { getShellApiInstance } from '../../common/shell-api-helpers'
import { authenticatedFetch } from '../auth'
import { PbxAvailableFeatureFlags, PbxFeatureFlagSetting } from './models'
import type { PbxFeatureFlag, PbxFeatureFlagService } from './models'
import { getJiveApiBaseUrl } from '../../core/environment'

export const getMeetingsFeatureFlag = async (pbxId: string) =>
  await getPbxFlag(pbxId, PbxAvailableFeatureFlags.MEETINGS)

export const hasMeetingsFeatureFlag = async (): Promise<boolean> =>
  await hasPbxFlagEnabled(PbxAvailableFeatureFlags.MEETINGS)

export const getStandardFeatureFlag = async (pbxId: string) =>
  await getPbxFlag(pbxId, PbxAvailableFeatureFlags.STANDARD)

export const hasStandardFeatureFlag = async (): Promise<boolean> =>
  await hasPbxFlagEnabled(PbxAvailableFeatureFlags.STANDARD)

export const getResolveFeatureFlag = async (pbxId: string) => await getPbxFlag(pbxId, PbxAvailableFeatureFlags.RESOLVE)

export const hasResolveFeatureFlag = async (): Promise<boolean> =>
  await hasPbxFlagEnabled(PbxAvailableFeatureFlags.RESOLVE)

/**
 * Checks if the pbx feature flag is enabled
 * @param featureFlag name of the pbx feature flag to check
 * @returns true if the feature flag is enabled, false otherwise
 */
export const hasPbxFlagEnabled = async (featureFlag: string): Promise<boolean> => {
  const context = getShellApiInstance().context
  const pbxId = context.pbx?.id

  if (!pbxId) {
    return false
  }

  if (context.pbx?.flags?.[featureFlag] !== undefined) {
    return context.pbx?.flags[featureFlag]
  } else {
    const fetchedFlag = await getPbxFlag(pbxId, featureFlag)
    return fetchedFlag?.setting === PbxFeatureFlagSetting.ENABLED
  }
}

/**
 * fetches all the enabled pbx feature flags for the given pbxId
 * @param pbxId string id of the pbx
 * @returns an object with the feature flags as keys and a boolean value indicating if the flag is enabled or not
 */
export const getPbxFlags = async (pbxId: string) => {
  const pbxFeaturesUrl = `${getJiveApiBaseUrl()}/features/v1/${pbxId}/enabled`
  let pbxFlags: { [key: string]: boolean } = {}

  const enabledFeatureFlagsResponse = await fetchFromFeaturesApi<PbxFeatureFlag>(pbxFeaturesUrl, {
    headers: { accept: 'application/json' },
    credentials: undefined,
  })

  if (enabledFeatureFlagsResponse) {
    pbxFlags = Object.entries(enabledFeatureFlagsResponse).reduce(
      (flags, [flagName, setting]) => ({ ...flags, [flagName]: setting === 'ENABLED' }),
      {},
    )
  }

  return pbxFlags
}

/**
 * fetches the value of the given pbx feature flag from the features api
 * @param pbxId - string id of the pbx
 * @param featureFlag - name of the pbx feature flag to fetch
 * @returns the value of the feature flag
 */
export const getPbxFlag = async (pbxId: string, featureFlag: string) => {
  const pbxFeaturesUrl = `${getJiveApiBaseUrl()}/features/v1/${pbxId}/${featureFlag}`

  try {
    if (!pbxId) {
      throw new Error('pbxId is empty')
    }

    const apiFlagValue = await fetchFromFeaturesApi<PbxFeatureFlagService>(pbxFeaturesUrl, {
      headers: { accept: 'application/json' },
      credentials: undefined,
    })

    return apiFlagValue
  } catch (error) {
    getShellLogger().error(`error in API call for ${featureFlag} PBX Feature Flag`, error)
  }
}

const fetchFromFeaturesApi = async <T>(url: string, init: RequestInit) => {
  try {
    const response = await authenticatedFetch<T>(url, init)
    return await response.json()
  } catch (error) {
    getShellLogger().error(`error in API call for ${url}`, error)
  }
}
